import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api } from '../trpc.js';
import {
  Alerta,
  Cartao,
  Carregando,
  ErroDeCarga,
  Tabela,
  Vazio,
  formatarMoeda,
} from '../comum/ui.js';

/**
 * RELATÓRIOS (§8) — HTML print-ready.
 *
 * "Print-ready" aqui é literal: o CSS tem `@media print` e as tabelas repetem
 * cabeçalho a cada página. A folha impressa é o que vai para a pasta do
 * cliente e para a fiscalização, então ela é um artefato de primeira classe,
 * não um `window.print()` no final.
 */

type TipoRelatorio =
  | 'folha-sintetica'
  | 'resumo-encargos'
  | 'extrato-rubrica'
  | 'fgts'
  | 'dctfweb'
  | 'memoria-fiscalizacao';

const RELATORIOS: Array<{ tipo: TipoRelatorio; nome: string; descricao: string }> = [
  { tipo: 'folha-sintetica', nome: 'Folha sintética', descricao: 'Uma linha por colaborador' },
  { tipo: 'resumo-encargos', nome: 'Resumo de encargos', descricao: 'INSS, FGTS, IRRF e guias' },
  { tipo: 'extrato-rubrica', nome: 'Extrato por rubrica', descricao: 'Total do mês por verba' },
  { tipo: 'fgts', nome: 'FGTS — apuração esperada', descricao: 'Para conferir contra a guia' },
  { tipo: 'dctfweb', nome: 'DCTFWeb — créditos esperados', descricao: 'Para conferir a apuração' },
  {
    tipo: 'memoria-fiscalizacao',
    nome: 'Memória para fiscalização',
    descricao: 'Cada valor com a conta escrita',
  },
];

export function Relatorios({ competencia }: { competencia: string }) {
  const [selecionado, setSelecionado] = useState<TipoRelatorio>('folha-sintetica');

  return (
    <div className="tela">
      <h1 className="nao-imprimir">Relatórios — {competencia}</h1>

      <nav className="abas nao-imprimir">
        {RELATORIOS.map((r) => (
          <button
            key={r.tipo}
            type="button"
            className={`aba ${selecionado === r.tipo ? 'aba--ativa' : ''}`}
            onClick={() => setSelecionado(r.tipo)}
          >
            <strong>{r.nome}</strong>
            <span className="sutil">{r.descricao}</span>
          </button>
        ))}
      </nav>

      <div className="acoes nao-imprimir">
        <button type="button" className="botao" onClick={() => window.print()}>
          Imprimir / salvar PDF
        </button>
      </div>

      {selecionado === 'folha-sintetica' ? <FolhaSintetica competencia={competencia} /> : null}
      {selecionado === 'resumo-encargos' ? <ResumoEncargos competencia={competencia} /> : null}
      {selecionado === 'extrato-rubrica' ? <ExtratoRubrica competencia={competencia} /> : null}
      {selecionado === 'fgts' ? <RelatorioFgts competencia={competencia} /> : null}
      {selecionado === 'dctfweb' ? <RelatorioDctfweb competencia={competencia} /> : null}
      {selecionado === 'memoria-fiscalizacao' ? <MemoriaFiscal competencia={competencia} /> : null}
    </div>
  );
}

function FolhaSintetica({ competencia }: { competencia: string }) {
  const c = useQuery({
    queryKey: ['rel-sintetica', competencia],
    queryFn: () => api.relatorios.folhaSintetica.query({ competencia }),
  });

  if (c.isLoading) return <Carregando o="relatório" />;
  if (c.error) return <ErroDeCarga erro={c.error} />;
  if (!c.data) return <Vazio mensagem="Sem dados." />;

  return (
    <Cartao titulo={`Folha sintética — ${competencia}`}>
      <Tabela
        cabecalhos={['Matrícula', 'Nome', '#Salário', '#Proventos', '#Descontos', '#INSS', '#IRRF', '#FGTS', '#Líquido']}
      >
        {c.data.linhas.map((l) => (
          <tr key={l.matricula}>
            <td>{l.matricula}</td>
            <td>{l.nome}</td>
            <td className="numerica">{formatarMoeda(l.salarioBase)}</td>
            <td className="numerica">{formatarMoeda(l.proventos)}</td>
            <td className="numerica">{formatarMoeda(l.descontos)}</td>
            <td className="numerica">{formatarMoeda(l.inss)}</td>
            <td className="numerica">{formatarMoeda(l.irrf)}</td>
            <td className="numerica">{formatarMoeda(l.fgts)}</td>
            <td className="numerica destaque">{formatarMoeda(l.liquido)}</td>
          </tr>
        ))}
        <tr className="linha--total">
          <td colSpan={3}>TOTAL</td>
          <td className="numerica">{formatarMoeda(c.data.totais.proventos)}</td>
          <td className="numerica">{formatarMoeda(c.data.totais.descontos)}</td>
          <td className="numerica">{formatarMoeda(c.data.totais.inss)}</td>
          <td className="numerica">{formatarMoeda(c.data.totais.irrf)}</td>
          <td className="numerica">{formatarMoeda(c.data.totais.fgts)}</td>
          <td className="numerica destaque">{formatarMoeda(c.data.totais.liquido)}</td>
        </tr>
      </Tabela>
    </Cartao>
  );
}

function ResumoEncargos({ competencia }: { competencia: string }) {
  const c = useQuery({
    queryKey: ['rel-encargos', competencia],
    queryFn: () => api.relatorios.resumoEncargos.query({ competencia }),
  });

  if (c.isLoading) return <Carregando o="relatório" />;
  if (c.error) return <ErroDeCarga erro={c.error} />;
  if (!c.data) return <Vazio mensagem="Sem dados." />;

  const d = c.data;

  return (
    <>
      <Cartao titulo={`Resumo de encargos — ${competencia}`}>
        <Tabela cabecalhos={['Item', '#Valor']}>
          <tr className="linha--secao">
            <td colSpan={2}>Descontado do empregado</td>
          </tr>
          <tr>
            <td>INSS mensal</td>
            <td className="numerica">{formatarMoeda(d.empregado.inss)}</td>
          </tr>
          <tr>
            <td>INSS sobre 13º</td>
            <td className="numerica">{formatarMoeda(d.empregado.inss13)}</td>
          </tr>
          <tr>
            <td>IRRF mensal</td>
            <td className="numerica">{formatarMoeda(d.empregado.irrf)}</td>
          </tr>
          <tr>
            <td>IRRF sobre 13º</td>
            <td className="numerica">{formatarMoeda(d.empregado.irrf13)}</td>
          </tr>

          <tr className="linha--secao">
            <td colSpan={2}>Custo da empresa</td>
          </tr>
          <tr>
            <td>CPP</td>
            <td className="numerica">{formatarMoeda(d.empresa.cpp)}</td>
          </tr>
          <tr>
            <td>RAT × FAP</td>
            <td className="numerica">{formatarMoeda(d.empresa.rat)}</td>
          </tr>
          <tr>
            <td>Terceiros</td>
            <td className="numerica">{formatarMoeda(d.empresa.terceiros)}</td>
          </tr>
          <tr>
            <td>FGTS</td>
            <td className="numerica">{formatarMoeda(d.empresa.fgts)}</td>
          </tr>

          <tr className="linha--total">
            <td>Custo total da folha</td>
            <td className="numerica destaque">{formatarMoeda(d.custoTotalFolha)}</td>
          </tr>
        </Tabela>
      </Cartao>

      <Cartao titulo="Guias a recolher">
        {d.guias.map((g) => (
          <div key={g.guia} className="guia">
            <div>
              <strong>{g.guia}</strong>
              <p className="sutil">{g.descricao}</p>
              <p className="sutil">{g.observacao}</p>
            </div>
            <span className="guia__valor">{formatarMoeda(g.valor)}</span>
          </div>
        ))}
      </Cartao>
    </>
  );
}

function ExtratoRubrica({ competencia }: { competencia: string }) {
  const c = useQuery({
    queryKey: ['rel-rubrica', competencia],
    queryFn: () => api.relatorios.extratoPorRubrica.query({ competencia }),
  });

  if (c.isLoading) return <Carregando o="relatório" />;
  if (c.error) return <ErroDeCarga erro={c.error} />;
  if (!c.data) return <Vazio mensagem="Sem dados." />;

  return (
    <Cartao titulo={`Extrato por rubrica — ${competencia}`}>
      <Tabela cabecalhos={['Código', 'Descrição', 'Tipo', '#Colaboradores', '#Quantidade', '#Total']}>
        {c.data.rubricas.map((r) => (
          <tr key={r.codigo}>
            <td>{r.codigo}</td>
            <td>{r.descricao}</td>
            <td>{r.tipo}</td>
            <td className="numerica">{r.quantidadeColaboradores}</td>
            <td className="numerica">{r.somaQuantidade ?? '—'}</td>
            <td className="numerica">{r.totalFormatado}</td>
          </tr>
        ))}
      </Tabela>
    </Cartao>
  );
}

function RelatorioFgts({ competencia }: { competencia: string }) {
  const c = useQuery({
    queryKey: ['rel-fgts', competencia],
    queryFn: () => api.relatorios.fgts.query({ competencia }),
  });

  if (c.isLoading) return <Carregando o="relatório" />;
  if (c.error) return <ErroDeCarga erro={c.error} />;
  if (!c.data) return <Vazio mensagem="Sem dados." />;

  return (
    <Cartao titulo={`FGTS — apuração esperada ${competencia}`}>
      <Alerta severidade="INFO" titulo="Uso deste relatório">
        {c.data.observacao}
      </Alerta>

      <Tabela cabecalhos={['Matrícula', 'Nome', '#Base', '#Mensal', '#13º', '#Aviso', '#Multa', '#Total']}>
        {c.data.linhas.map((l) => (
          <tr key={l.matricula}>
            <td>{l.matricula}</td>
            <td>{l.nome}</td>
            <td className="numerica">{formatarMoeda(l.baseMensal)}</td>
            <td className="numerica">{formatarMoeda(l.depositoMensal)}</td>
            <td className="numerica">{formatarMoeda(l.deposito13)}</td>
            <td className="numerica">{formatarMoeda(l.depositoAviso)}</td>
            <td className="numerica">{formatarMoeda(l.multaRescisoria)}</td>
            <td className="numerica destaque">{formatarMoeda(l.total)}</td>
          </tr>
        ))}
        <tr className="linha--total">
          <td colSpan={7}>TOTAL DE DEPÓSITOS</td>
          <td className="numerica destaque">{formatarMoeda(c.data.totalDeposito)}</td>
        </tr>
      </Tabela>
    </Cartao>
  );
}

function RelatorioDctfweb({ competencia }: { competencia: string }) {
  const c = useQuery({
    queryKey: ['rel-dctfweb', competencia],
    queryFn: () => api.relatorios.dctfweb.query({ competencia }),
  });

  if (c.isLoading) return <Carregando o="relatório" />;
  if (c.error) return <ErroDeCarga erro={c.error} />;
  if (!c.data) return <Vazio mensagem="Sem dados." />;

  return (
    <Cartao titulo={`DCTFWeb — créditos esperados ${competencia}`}>
      <Alerta severidade="INFO" titulo="Uso deste relatório">
        {c.data.observacao}
      </Alerta>

      <Tabela cabecalhos={['Código', 'Descrição', '#Valor']}>
        {c.data.creditos.map((cr) => (
          <tr key={`${cr.codigo}-${cr.descricao}`}>
            <td>{cr.codigo}</td>
            <td>{cr.descricao}</td>
            <td className="numerica">{formatarMoeda(cr.valor)}</td>
          </tr>
        ))}
        <tr className="linha--total">
          <td colSpan={2}>TOTAL</td>
          <td className="numerica destaque">{formatarMoeda(c.data.total)}</td>
        </tr>
      </Tabela>
    </Cartao>
  );
}

function MemoriaFiscal({ competencia }: { competencia: string }) {
  const c = useQuery({
    queryKey: ['rel-memoria', competencia],
    queryFn: () => api.relatorios.memoriaFiscalizacao.query({ competencia }),
  });

  if (c.isLoading) return <Carregando o="relatório" />;
  if (c.error) return <ErroDeCarga erro={c.error} />;
  if (!c.data) return <Vazio mensagem="Sem dados." />;

  return (
    <Cartao titulo={`Memória de cálculo para fiscalização — ${competencia}`}>
      <Alerta severidade="INFO" titulo="Declaração de reprodutibilidade">
        {c.data.advertencia}
      </Alerta>

      {c.data.colaboradores.map((col) => (
        <article key={col.matricula} className="memoria-fiscal">
          <h3>
            {col.nome} — matrícula {col.matricula} — CPF {col.cpf}
          </h3>
          <Tabela cabecalhos={['Rubrica', '#Valor', '#Base', 'Tabela', 'Fórmula aplicada']}>
            {col.linhas.map((l, i) => (
              <tr key={`${l.rubrica}-${i}`}>
                <td>
                  {l.rubrica}
                  <br />
                  <span className="sutil">{l.descricao}</span>
                </td>
                <td className="numerica">{formatarMoeda(l.valor)}</td>
                <td className="numerica">{formatarMoeda(l.base)}</td>
                <td>{l.tabelaVersao}</td>
                <td className="formula">{l.formula}</td>
              </tr>
            ))}
          </Tabela>
        </article>
      ))}
    </Cartao>
  );
}
